/**
 * Map theme registry.
 *
 * A map theme changes the look and feel of the canvas: the background,
 * the nodes, the connection lines, and the node font. The active theme is
 * stored per map (in the map's shared Yjs `meta` map under the `theme`
 * key) so every collaborator sees the same look, and it persists with the
 * map. Themes are a pure rendering layer: switching themes never rewrites
 * stored node colors, so switching back always restores the previous look.
 *
 * Every theme exposes the same ordered semantic color slots. The picker may
 * show a theme-adjusted tone, but selecting it stores the canonical COLORS
 * gradient. A theme switch therefore changes presentation without changing
 * which color slot a node belongs to.
 */

import { COLORS, LEGACY_COLORS } from './colors';

export const DEFAULT_THEME_ID = 'default';

const DEFAULT_NODE_FONT_FAMILY = 'Arial, sans-serif';

/**
 * Extract the first hex color from a stored node color value
 * (usually a CSS gradient string, occasionally a bare hex).
 */
export function extractFirstHex(colorValue) {
  if (typeof colorValue !== 'string') return null;
  const match = colorValue.match(/#[0-9a-fA-F]{6}/);
  return match ? match[0] : null;
}

function hexToRgb(hex) {
  return {
    r: parseInt(hex.slice(1, 3), 16),
    g: parseInt(hex.slice(3, 5), 16),
    b: parseInt(hex.slice(5, 7), 16),
  };
}

function relativeLuminance(hex) {
  const { r, g, b } = hexToRgb(hex);
  const channels = [r, g, b].map((value) => {
    const channel = value / 255;
    return channel <= 0.04045
      ? channel / 12.92
      : ((channel + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
}

function contrastRatio(firstHex, secondHex) {
  const first = relativeLuminance(firstHex);
  const second = relativeLuminance(secondHex);
  return (Math.max(first, second) + 0.05) / (Math.min(first, second) + 0.05);
}

function readableNodeText(background, lightText, darkText) {
  const stops = typeof background === 'string'
    ? background.match(/#[0-9a-fA-F]{6}/g) || []
    : [];
  if (stops.length === 0) return lightText;

  const minimumContrast = (textColor) => (
    Math.min(...stops.map((stop) => contrastRatio(stop, textColor)))
  );
  return minimumContrast(darkText) > minimumContrast(lightText)
    ? darkText
    : lightText;
}

function rgbToHex({ r, g, b }) {
  const channel = (value) => Math.max(0, Math.min(255, Math.round(value)))
    .toString(16)
    .padStart(2, '0');
  return `#${channel(r)}${channel(g)}${channel(b)}`;
}

/** Mix a color toward a target; amount 0 keeps the color, 1 is the target. */
function mixToward({ r, g, b }, target, amount) {
  return {
    r: Math.round(r + (target.r - r) * amount),
    g: Math.round(g + (target.g - g) * amount),
    b: Math.round(b + (target.b - b) * amount),
  };
}

function transformGradient(gradient, transform) {
  return gradient.replace(/#[0-9a-fA-F]{6}/g, (hex) => rgbToHex(transform(hexToRgb(hex))));
}

function buildSemanticPalette(transform) {
  return COLORS.map((color, slot) => ({
    name: color.name,
    gradient: transformGradient(color.gradient, transform),
    canonicalGradient: color.gradient,
    slot,
  }));
}

const WARM_CHALK = { r: 255, g: 239, b: 215 };
const WARM_PAPER = { r: 255, g: 248, b: 235 };
const BLUEPRINT_INK = { r: 135, g: 215, b: 235 };
const DUSK_GLOW = { r: 224, g: 151, b: 188 };
const GARDEN_MIST = { r: 214, g: 229, b: 205 };
const TERMINAL_GLOW = { r: 187, g: 241, b: 207 };

/**
 * Turn a stored node color into a "colored in with chalk" fill: the
 * same semantic hue warmed and lifted toward pastel, shaded so the middle
 * looks pressed on harder than the edges.
 */
export function chalkFillFromColor(colorValue) {
  const hex = extractFirstHex(colorValue);
  const base = hex ? hexToRgb(hex) : { r: 244, g: 241, b: 232 };
  const chalk = mixToward(base, WARM_CHALK, 0.56);
  const { r, g, b } = chalk;
  return (
    `radial-gradient(ellipse at 50% 42%, rgba(${r}, ${g}, ${b}, 0.52) 0%, ` +
    `rgba(${r}, ${g}, ${b}, 0.30) 62%, rgba(${r}, ${g}, ${b}, 0.16) 100%)`
  );
}

// Retained only to interpret maps colored with the first Chalkboard release.
// New selections store canonical COLORS gradients.
const LEGACY_CHALKBOARD_PALETTE = [
  { name: 'Chalk White', gradient: 'linear-gradient(135deg, #f4f1e8 0%, #e6e2d4 100%)' },
  { name: 'Chalk Yellow', gradient: 'linear-gradient(135deg, #f5e6a8 0%, #ecd88a 100%)' },
  { name: 'Chalk Pink', gradient: 'linear-gradient(135deg, #f2c4cf 0%, #e8aebd 100%)' },
  { name: 'Chalk Blue', gradient: 'linear-gradient(135deg, #aecbe8 0%, #93b7dd 100%)' },
  { name: 'Chalk Green', gradient: 'linear-gradient(135deg, #b8d8bc 0%, #9fc7a5 100%)' },
  { name: 'Chalk Orange', gradient: 'linear-gradient(135deg, #f4cfa5 0%, #ecbd85 100%)' },
  { name: 'Chalk Lavender', gradient: 'linear-gradient(135deg, #cdc1e8 0%, #b7a8dd 100%)' },
  { name: 'Chalk Mint', gradient: 'linear-gradient(135deg, #b2e0d4 0%, #97d1c1 100%)' },
  { name: 'Chalk Red', gradient: 'linear-gradient(135deg, #eaa9a0 0%, #e08f84 100%)' },
  { name: 'Chalk Grey', gradient: 'linear-gradient(135deg, #d0d3d1 0%, #b9bdbb 100%)' },
];

export const CHALKBOARD_PALETTE = buildSemanticPalette(
  (rgb) => mixToward(rgb, WARM_CHALK, 0.56),
);
export const PAPER_PALETTE = buildSemanticPalette(
  (rgb) => mixToward(rgb, WARM_PAPER, 0.38),
);
export const BLUEPRINT_PALETTE = buildSemanticPalette(
  (rgb) => mixToward(rgb, BLUEPRINT_INK, 0.04),
);
export const DUSK_PALETTE = buildSemanticPalette(
  (rgb) => mixToward(rgb, DUSK_GLOW, 0.05),
);
export const GARDEN_PALETTE = buildSemanticPalette(
  (rgb) => mixToward(rgb, GARDEN_MIST, 0.34),
);
export const MINIMALIST_PALETTE = buildSemanticPalette(
  (rgb) => mixToward(rgb, TERMINAL_GLOW, 0.46),
);

const CHALK_LINE = 'rgba(240, 240, 234, 0.72)';
const CHALK_LINE_SELECTED = 'rgba(255, 255, 255, 0.95)';
const CHALK_TEXT = 'rgba(250, 250, 245, 0.96)';
const CHALK_BORDER = 'rgba(245, 245, 238, 0.85)';

// Slate green-black with a faint dusting of chalk residue.
const CHALKBOARD_BACKGROUND =
  'radial-gradient(ellipse at 18% 22%, rgba(255, 255, 255, 0.045) 0%, rgba(255, 255, 255, 0) 42%), ' +
  'radial-gradient(ellipse at 78% 70%, rgba(255, 255, 255, 0.035) 0%, rgba(255, 255, 255, 0) 46%), ' +
  'radial-gradient(ellipse at 45% 95%, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0) 38%), ' +
  'linear-gradient(160deg, #2b2f2c 0%, #242825 55%, #202421 100%)';

const PAPER_BACKGROUND =
  'radial-gradient(circle at 1px 1px, rgba(94, 74, 52, 0.12) 1px, transparent 1.2px), ' +
  'linear-gradient(145deg, #fffaf0 0%, #f6ecdc 100%)';
const BLUEPRINT_BACKGROUND =
  'linear-gradient(rgba(116, 197, 224, 0.13) 1px, transparent 1px), ' +
  'linear-gradient(90deg, rgba(116, 197, 224, 0.13) 1px, transparent 1px), ' +
  'linear-gradient(rgba(116, 197, 224, 0.055) 1px, transparent 1px), ' +
  'linear-gradient(90deg, rgba(116, 197, 224, 0.055) 1px, transparent 1px), #0b3150';
const DUSK_BACKGROUND =
  'linear-gradient(155deg, #2a2131 0%, #202235 42%, #172936 72%, #122d35 100%)';
const GARDEN_BACKGROUND =
  'radial-gradient(circle at 15% 12%, rgba(255, 255, 255, 0.72), transparent 30%), ' +
  'radial-gradient(circle at 85% 82%, rgba(122, 153, 112, 0.12), transparent 38%), ' +
  'linear-gradient(150deg, #edf3e8 0%, #dce8d7 100%)';
const MINIMALIST_BACKGROUND =
  'repeating-linear-gradient(0deg, transparent 0, transparent 23px, rgba(150, 205, 170, 0.022) 24px), ' +
  'repeating-linear-gradient(90deg, transparent 0, transparent 23px, rgba(150, 205, 170, 0.018) 24px), #090d0b';

const DEFAULT_DISCUSSION_PALETTE = {
  light: {
    panel: '#ffffff',
    raised: '#f8fafc',
    border: '#dbe2ea',
    text: '#111827',
    muted: '#64748b',
    accent: '#2563eb',
    accentText: '#ffffff',
    input: '#ffffff',
  },
  dark: {
    panel: '#111827',
    raised: '#1f2937',
    border: '#374151',
    text: '#f9fafb',
    muted: '#9ca3af',
    accent: '#60a5fa',
    accentText: '#111827',
    input: '#0f172a',
  },
};

const CHALKBOARD_DISCUSSION_PALETTE = {
  panel: CHALKBOARD_BACKGROUND,
  raised: '#303631',
  border: 'rgba(245, 245, 238, 0.28)',
  text: CHALK_TEXT,
  muted: 'rgba(240, 240, 234, 0.68)',
  accent: '#aecbe8',
  accentText: '#182129',
  input: '#202421',
};
const PAPER_DISCUSSION_PALETTE = {
  panel: PAPER_BACKGROUND,
  raised: '#f0e3d0',
  border: 'rgba(93, 70, 46, 0.28)',
  text: '#2f2923',
  muted: '#746452',
  accent: '#765038',
  accentText: '#ffffff',
  input: '#fffdf8',
};
const BLUEPRINT_DISCUSSION_PALETTE = {
  panel: BLUEPRINT_BACKGROUND,
  raised: '#0e3d62',
  border: 'rgba(151, 220, 242, 0.34)',
  text: '#f3fbff',
  muted: '#a4cddd',
  accent: '#89d5ea',
  accentText: '#0a2940',
  input: '#082b47',
};
const DUSK_DISCUSSION_PALETTE = {
  panel: DUSK_BACKGROUND,
  raised: '#24213a',
  border: 'rgba(255, 222, 240, 0.22)',
  text: '#fff8fc',
  muted: '#c4adc0',
  accent: '#e18ab2',
  accentText: '#271522',
  input: '#121728',
};
const GARDEN_DISCUSSION_PALETTE = {
  panel: GARDEN_BACKGROUND,
  raised: '#d8e4d3',
  border: 'rgba(69, 93, 67, 0.25)',
  text: '#263128',
  muted: '#5f705f',
  accent: '#456947',
  accentText: '#ffffff',
  input: '#f8fbf5',
};
const MINIMALIST_DISCUSSION_PALETTE = {
  panel: '#090d0b',
  raised: '#111813',
  border: 'rgba(157, 225, 181, 0.20)',
  text: '#d9e7de',
  muted: '#82968a',
  accent: '#8ee3ad',
  accentText: '#071009',
  input: '#0d120f',
};

const PALETTE_VARIANTS = [
  COLORS,
  LEGACY_COLORS,
  LEGACY_CHALKBOARD_PALETTE,
  CHALKBOARD_PALETTE,
  PAPER_PALETTE,
  BLUEPRINT_PALETTE,
  DUSK_PALETTE,
  GARDEN_PALETTE,
  MINIMALIST_PALETTE,
];

function colorGradient(color) {
  if (typeof color === 'string') return color;
  return color?.gradient || null;
}

/** Resolve a canonical slot from current and legacy theme-specific gradients. */
export function getSemanticColorSlot(color) {
  const gradient = colorGradient(color);
  if (!gradient) return -1;
  for (const palette of PALETTE_VARIANTS) {
    const slot = palette.findIndex((candidate) => candidate.gradient === gradient);
    if (slot >= 0 && slot < COLORS.length) return slot;
  }
  return -1;
}

/** Return the canonical color object that should be persisted for a selection. */
export function getCanonicalPaletteColor(color) {
  const slot = Number.isInteger(color?.slot) ? color.slot : getSemanticColorSlot(color);
  if (slot >= 0 && slot < COLORS.length) return COLORS[slot];
  return color;
}

/** Reinterpret a stored canonical or legacy color through the active theme. */
export function getThemeNodeColor(theme, color) {
  const gradient = colorGradient(color);
  const slot = getSemanticColorSlot(color);
  if (slot < 0) return gradient;

  // Preserve the exact appearance of maps created before the semantic palette
  // when they use the Default theme. Other themes reinterpret the historical
  // picker position so switching themes remains predictable.
  if (
    theme?.id === DEFAULT_THEME_ID
    && LEGACY_COLORS.some((legacyColor) => legacyColor.gradient === gradient)
  ) {
    return gradient;
  }

  return theme?.palette?.[slot]?.gradient || COLORS[slot].gradient;
}

/**
 * Theme contract:
 * - id / name / description / access: identity, menu copy, and future tier
 *   assignment. `unassigned` themes remain unlocked while the set is reviewed.
 * - canvas.getBackground(isDarkMode): CSS background for the map canvas
 *   (and thumbnails). The default theme follows the light/dark toggle;
 *   opinionated themes may ignore it.
 * - canvas.getSolidColor(isDarkMode): plain fallback color for surfaces
 *   that cannot render a CSS gradient (image export, thumbnails).
 * - canvas.getForeground(isDarkMode): readable floating canvas controls.
 * - node.fontFamily: node text font (also used when measuring node text);
 *   null keeps the app default.
 * - node.getStyle(ctx): style overrides merged over the standard node
 *   style, or null for no change. ctx: { color, isRoot, isSelected,
 *   isHighlighted, isDragging }.
 * - edge.getStyle(ctx): { stroke, strokeWidth } for connection lines, or
 *   null to keep the standard treatment. ctx: { isDarkMode, isSelected }.
 * - discussion.getPalette(isDarkMode): themed collaboration panel surfaces.
 * - palette: node colors the picker offers while this theme is active.
 * - preview: colors for the theme switcher swatch.
 */
export const MAP_THEMES = {
  [DEFAULT_THEME_ID]: {
    id: DEFAULT_THEME_ID,
    name: 'Default',
    description: 'The classic Mindmappi look.',
    access: { tier: 'free' },
    canvas: {
      getBackground: (isDarkMode) => (isDarkMode ? '#1a1a1a' : '#ffffff'),
      getSolidColor: (isDarkMode) => (isDarkMode ? '#1a1a1a' : '#ffffff'),
      getForeground: (isDarkMode) => (isDarkMode ? '#ffffff' : '#111827'),
    },
    node: {
      fontFamily: null,
      getStyle: () => null,
    },
    edge: {
      getStyle: () => null,
    },
    discussion: {
      getPalette: (isDarkMode) => (
        isDarkMode ? DEFAULT_DISCUSSION_PALETTE.dark : DEFAULT_DISCUSSION_PALETTE.light
      ),
    },
    palette: COLORS,
    preview: {
      background: '#ffffff',
      node: COLORS[0].gradient,
      nodeText: '#ffffff',
      border: '#d1d5db',
    },
  },

  chalkboard: {
    id: 'chalkboard',
    name: 'Chalkboard',
    description: 'Warm coloured chalk on a dark board.',
    access: { tier: 'unassigned' },
    canvas: {
      getBackground: () => CHALKBOARD_BACKGROUND,
      getSolidColor: () => '#242825',
      getForeground: () => '#f5f5ee',
    },
    node: {
      fontFamily: "'Patrick Hand', 'Segoe Print', 'Comic Sans MS', cursive",
      getStyle: ({ color, isSelected, isHighlighted, isDragging }) => ({
        background: chalkFillFromColor(color),
        border: `2px solid ${CHALK_BORDER}`,
        color: CHALK_TEXT,
        // Slightly uneven corners read as hand-drawn without any filters.
        borderRadius: '46px 38px 44px 40px / 38px 44px 40px 46px',
        boxShadow: (isSelected || isDragging)
          ? '0 0 14px rgba(255, 255, 255, 0.28), 0 0 2px rgba(255, 255, 255, 0.4)'
          : isHighlighted
            ? '0 0 0 3px rgba(255, 255, 255, 0.35), 0 0 10px rgba(255, 255, 255, 0.18)'
            : '0 0 6px rgba(255, 255, 255, 0.08)',
      }),
    },
    edge: {
      getStyle: ({ isSelected }) => ({
        stroke: isSelected ? CHALK_LINE_SELECTED : CHALK_LINE,
        strokeWidth: isSelected ? 3 : 2.25,
      }),
    },
    discussion: {
      getPalette: () => CHALKBOARD_DISCUSSION_PALETTE,
    },
    palette: CHALKBOARD_PALETTE,
    preview: {
      background: '#262a27',
      node: 'transparent',
      nodeText: CHALK_TEXT,
      border: CHALK_BORDER,
    },
  },

  paper: {
    id: 'paper',
    name: 'Paper',
    description: 'Warm paper, soft ink and familiar colour slots.',
    access: { tier: 'unassigned' },
    canvas: {
      getBackground: () => PAPER_BACKGROUND,
      getSolidColor: () => '#f8efe0',
      getForeground: () => '#3f342a',
    },
    node: {
      fontFamily: "Georgia, 'Times New Roman', serif",
      getStyle: ({ themeColor, isSelected, isHighlighted, isDragging }) => ({
        background: themeColor,
        color: readableNodeText(themeColor, '#ffffff', '#000000'),
        border: '1px solid rgba(93, 70, 46, 0.34)',
        borderRadius: '18px 22px 19px 24px / 21px 18px 23px 19px',
        boxShadow: (isSelected || isDragging)
          ? '0 8px 20px rgba(91, 65, 38, 0.24), 0 0 0 2px rgba(111, 82, 50, 0.18)'
          : isHighlighted
            ? '0 0 0 3px rgba(111, 82, 50, 0.20)'
            : '0 3px 9px rgba(91, 65, 38, 0.13)',
      }),
    },
    edge: {
      getStyle: ({ isSelected }) => ({
        stroke: isSelected ? 'rgba(72, 52, 35, 0.86)' : 'rgba(91, 69, 49, 0.55)',
        strokeWidth: isSelected ? 3 : 2,
      }),
    },
    discussion: {
      getPalette: () => PAPER_DISCUSSION_PALETTE,
    },
    palette: PAPER_PALETTE,
    preview: {
      background: '#f8efe0',
      node: PAPER_PALETTE[0].gradient,
      nodeText: '#2f2923',
      border: 'rgba(93, 70, 46, 0.42)',
    },
  },

  blueprint: {
    id: 'blueprint',
    name: 'Blueprint',
    description: 'Technical grid paper with bright drafted lines.',
    access: { tier: 'unassigned' },
    canvas: {
      getBackground: () => BLUEPRINT_BACKGROUND,
      getSolidColor: () => '#0b3150',
      getForeground: () => '#f3fbff',
    },
    node: {
      fontFamily: "'Courier New', Courier, monospace",
      getStyle: ({ themeColor, isSelected, isHighlighted, isDragging }) => ({
        background: themeColor,
        color: '#f3fbff',
        border: '1.5px solid rgba(190, 237, 250, 0.82)',
        borderRadius: '8px',
        boxShadow: (isSelected || isDragging)
          ? '0 0 0 2px rgba(172, 229, 248, 0.34), 0 8px 18px rgba(0, 12, 26, 0.34)'
          : isHighlighted
            ? '0 0 0 3px rgba(172, 229, 248, 0.26)'
            : '0 4px 12px rgba(0, 12, 26, 0.24)',
      }),
    },
    edge: {
      getStyle: ({ isSelected }) => ({
        stroke: isSelected ? '#d9f6ff' : 'rgba(151, 220, 242, 0.76)',
        strokeWidth: isSelected ? 3 : 2,
      }),
    },
    discussion: {
      getPalette: () => BLUEPRINT_DISCUSSION_PALETTE,
    },
    palette: BLUEPRINT_PALETTE,
    preview: {
      background: '#0b3150',
      node: BLUEPRINT_PALETTE[0].gradient,
      nodeText: '#f3fbff',
      border: '#a8e2f5',
    },
  },

  dusk: {
    id: 'dusk',
    name: 'Dusk',
    description: 'A low-light canvas with softly luminous colours.',
    access: { tier: 'unassigned' },
    canvas: {
      getBackground: () => DUSK_BACKGROUND,
      getSolidColor: () => '#17182a',
      getForeground: () => '#fff8fc',
    },
    node: {
      fontFamily: "'Quicksand', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      getStyle: ({ themeColor, isSelected, isHighlighted, isDragging }) => ({
        background: themeColor,
        color: '#fff8fc',
        border: '1px solid rgba(255, 222, 240, 0.30)',
        borderRadius: '28px',
        boxShadow: (isSelected || isDragging)
          ? '0 0 0 2px rgba(255, 205, 231, 0.22), 0 10px 26px rgba(4, 5, 18, 0.46)'
          : isHighlighted
            ? '0 0 0 3px rgba(255, 205, 231, 0.18)'
            : '0 6px 18px rgba(4, 5, 18, 0.32)',
      }),
    },
    edge: {
      getStyle: ({ isSelected }) => ({
        stroke: isSelected ? 'rgba(255, 226, 242, 0.94)' : 'rgba(202, 180, 224, 0.58)',
        strokeWidth: isSelected ? 3 : 2.15,
      }),
    },
    discussion: {
      getPalette: () => DUSK_DISCUSSION_PALETTE,
    },
    palette: DUSK_PALETTE,
    preview: {
      background: '#17182a',
      node: DUSK_PALETTE[0].gradient,
      nodeText: '#fff8fc',
      border: 'rgba(255, 222, 240, 0.45)',
    },
  },

  garden: {
    id: 'garden',
    name: 'Garden',
    description: 'A quiet sage canvas with softened natural tones.',
    access: { tier: 'unassigned' },
    canvas: {
      getBackground: () => GARDEN_BACKGROUND,
      getSolidColor: () => '#e3ecde',
      getForeground: () => '#263128',
    },
    node: {
      fontFamily: "'Quicksand', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      getStyle: ({ themeColor, isSelected, isHighlighted, isDragging }) => ({
        background: themeColor,
        color: readableNodeText(themeColor, '#ffffff', '#000000'),
        border: '1px solid rgba(69, 93, 67, 0.30)',
        borderRadius: '22px',
        boxShadow: (isSelected || isDragging)
          ? '0 8px 20px rgba(48, 72, 49, 0.22), 0 0 0 2px rgba(75, 103, 74, 0.16)'
          : isHighlighted
            ? '0 0 0 3px rgba(75, 103, 74, 0.17)'
            : '0 4px 12px rgba(48, 72, 49, 0.13)',
      }),
    },
    edge: {
      getStyle: ({ isSelected }) => ({
        stroke: isSelected ? 'rgba(42, 69, 45, 0.82)' : 'rgba(58, 87, 61, 0.48)',
        strokeWidth: isSelected ? 3 : 2,
      }),
    },
    discussion: {
      getPalette: () => GARDEN_DISCUSSION_PALETTE,
    },
    palette: GARDEN_PALETTE,
    preview: {
      background: '#e3ecde',
      node: GARDEN_PALETTE[0].gradient,
      nodeText: '#263128',
      border: 'rgba(69, 93, 67, 0.42)',
    },
  },

  minimalist: {
    id: 'minimalist',
    name: 'Minimalist',
    description: 'Compact framed ideas on a quiet terminal-inspired canvas.',
    access: { tier: 'unassigned' },
    canvas: {
      getBackground: () => MINIMALIST_BACKGROUND,
      getSolidColor: () => '#090d0b',
      getForeground: () => '#d9e7de',
    },
    node: {
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', monospace",
      horizontalPadding: 5,
      minimumTextSample: 'MM',
      getStyle: ({ themeColor, isSelected, isHighlighted, isDragging, isEditing, isCardNode }) => {
        const active = isSelected || isDragging || isEditing;
        return {
          background: active ? 'rgba(17, 24, 19, 0.88)' : 'transparent',
          color: extractFirstHex(themeColor) || '#b7e9c9',
          border: !isCardNode || active || isHighlighted
            ? '1px solid rgba(157, 225, 181, 0.44)'
            : '1px solid transparent',
          borderRadius: '7px',
          boxShadow: active
            ? '0 0 0 1px rgba(157, 225, 181, 0.05), 0 7px 18px rgba(0, 0, 0, 0.24)'
            : isHighlighted
              ? '0 0 12px rgba(126, 219, 159, 0.10)'
              : 'none',
        };
      },
    },
    edge: {
      getStyle: ({ isSelected }) => ({
        stroke: isSelected ? 'rgba(174, 237, 197, 0.88)' : 'rgba(115, 145, 126, 0.50)',
        strokeWidth: isSelected ? 2.5 : 1.5,
      }),
    },
    discussion: {
      getPalette: () => MINIMALIST_DISCUSSION_PALETTE,
    },
    palette: MINIMALIST_PALETTE,
    preview: {
      background: '#090d0b',
      node: 'rgba(17, 24, 19, 0.88)',
      nodeText: '#b7e9c9',
      border: 'rgba(157, 225, 181, 0.58)',
    },
  },
};

export const MAP_THEME_LIST = Object.values(MAP_THEMES);

/** Resolve a theme id to a theme, falling back to the default theme. */
export function getMapTheme(themeId) {
  return MAP_THEMES[themeId] || MAP_THEMES[DEFAULT_THEME_ID];
}

/** CSS background for the map canvas under a theme. */
export function getThemeCanvasBackground(theme, isDarkMode) {
  return theme?.canvas?.getBackground
    ? theme.canvas.getBackground(isDarkMode)
    : (isDarkMode ? '#1a1a1a' : '#ffffff');
}

/** Solid fallback color for exports/thumbnails under a theme. */
export function getThemeCanvasSolidColor(theme, isDarkMode) {
  return theme?.canvas?.getSolidColor
    ? theme.canvas.getSolidColor(isDarkMode)
    : (isDarkMode ? '#1a1a1a' : '#ffffff');
}

/** Readable foreground for controls that float directly over the canvas. */
export function getThemeCanvasForeground(theme, isDarkMode) {
  return theme?.canvas?.getForeground
    ? theme.canvas.getForeground(isDarkMode)
    : (isDarkMode ? '#ffffff' : '#111827');
}

/** Node text font for a theme, for both rendering and text measurement. */
export function getThemeNodeFontFamily(theme) {
  return theme?.node?.fontFamily || DEFAULT_NODE_FONT_FAMILY;
}

/** Node style overrides for a theme, or null when the theme keeps defaults. */
export function getThemeNodeStyle(theme, context) {
  const style = theme?.node?.getStyle?.(context) || null;
  if (/^#[0-9a-f]{6}$/i.test(context?.color || '')) {
    return { ...style, background: context.color, color: readableNodeText(context.color, '#ffffff', '#000000') };
  }
  return style;
}

/** Edge style overrides for a theme, or null when the theme keeps defaults. */
export function getThemeEdgeStyle(theme, context) {
  if (!theme?.edge?.getStyle) return null;
  return theme.edge.getStyle(context) || null;
}

/** Collaboration panel colors for a theme, with a mode-aware safe fallback. */
export function getThemeDiscussionPalette(theme, isDarkMode) {
  if (theme?.discussion?.getPalette) {
    return theme.discussion.getPalette(isDarkMode);
  }
  return isDarkMode ? DEFAULT_DISCUSSION_PALETTE.dark : DEFAULT_DISCUSSION_PALETTE.light;
}
